<script setup>
import { computed, markRaw, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker'
import { previewReportPdf } from '../../api/reportApi.js'
import { calculateFitWidthScale, clampPdfScale, getPageAtScrollPosition } from '../pdfViewerUtils.js'
import { createPdfCacheKey, includeTemplateInFilename } from '../reportTemplateUtils.js'
import PdfPageCanvas from './PdfPageCanvas.vue'
import { createReportPdfRequest } from '../reportPdfRequest.js'

let pdfjsModulePromise = null

function loadPdfjsModule() {
  if (!pdfjsModulePromise) {
    pdfjsModulePromise = import('pdfjs-dist/build/pdf.mjs').then((module) => {
      module.GlobalWorkerOptions.workerPort = new PdfWorker()
      return module
    })
  }
  return pdfjsModulePromise
}

const props = defineProps({
  reportId: { type: String, required: true },
  templateId: { type: String, required: true },
  templateVersion: { type: String, required: true },
  templateName: { type: String, required: true }
})
const emit = defineEmits(['loading', 'ready', 'failed'])

const scrollContainer = ref(null)
const pdfDocument = shallowRef(null)
const basePageSize = shallowRef({ width: 595, height: 842 })
const pageCount = ref(0)
const currentPage = ref(1)
const scale = ref(1)
const isFitWidth = ref(true)
const isLoading = ref(false)
const error = ref('')
const renderWarning = ref('')
const zoomPercent = computed(() => `${Math.round(scale.value * 100)}%`)
let sourceBlob = null
let sourceFilename = null
let readyPdfKey = null
let loadingTask = null
let resizeObserver = null
let loadVersion = 0
let basePageWidth = 0
const pdfRequests = createReportPdfRequest(previewReportPdf)

function clearPdf() {
  loadingTask?.destroy()
  loadingTask = null
  pdfDocument.value?.destroy()
  pdfDocument.value = null
  basePageSize.value = { width: 595, height: 842 }
  sourceBlob = null
  sourceFilename = null
  readyPdfKey = null
  pageCount.value = 0
  currentPage.value = 1
  basePageWidth = 0
}

function applyFitWidth() {
  if (!scrollContainer.value || !basePageWidth) return
  scale.value = calculateFitWidthScale(scrollContainer.value.clientWidth, basePageWidth)
}

function observeScrollContainer(container) {
  resizeObserver?.disconnect()
  if (container) resizeObserver?.observe(container)
}

async function loadPdf() {
  const version = ++loadVersion
  clearPdf()
  isLoading.value = true
  error.value = ''
  renderWarning.value = ''
  isFitWidth.value = true
  emit('loading', { templateId: props.templateId })

  try {
    const download = await pdfRequests.load(props.reportId, props.templateId, props.templateVersion)
    if (!download || version !== loadVersion) return
    const bytes = new Uint8Array(await download.blob.arrayBuffer())
    if (version !== loadVersion) return
    const { getDocument } = await loadPdfjsModule()
    if (version !== loadVersion) return
    loadingTask = getDocument({ data: bytes })
    const document = await loadingTask.promise
    if (version !== loadVersion) {
      document.destroy()
      return
    }
    const firstPage = await document.getPage(1)
    if (version !== loadVersion) {
      document.destroy()
      return
    }
    const firstPageViewport = firstPage.getViewport({ scale: 1 })
    basePageWidth = firstPageViewport.width
    basePageSize.value = { width: firstPageViewport.width, height: firstPageViewport.height }
    readyPdfKey = createPdfCacheKey(props.reportId, props.templateId, props.templateVersion)
    sourceBlob = download.blob
    sourceFilename = includeTemplateInFilename(
      download.filename || `건강자산_보장분석_${props.reportId}.pdf`,
      props.templateId
    )
    pdfDocument.value = markRaw(document)
    pageCount.value = document.numPages
    currentPage.value = 1
    applyFitWidth()
    scrollContainer.value?.scrollTo({ top: 0, behavior: 'auto' })
    emit('ready', { filename: sourceFilename, templateId: props.templateId })
  } catch (cause) {
    if (version === loadVersion && cause.name !== 'AbortError') {
      error.value = cause.message || 'PDF 미리보기를 불러오지 못했습니다.'
      emit('failed', error.value)
    }
  } finally {
    if (version === loadVersion) isLoading.value = false
  }
}

function zoomBy(multiplier) {
  isFitWidth.value = false
  scale.value = clampPdfScale(scale.value * multiplier)
}

// Page preloading must never change the toolbar page number. Only the viewer's
// actual scroll position (or an explicit navigation click) can change it.
function syncCurrentPageFromScroll() {
  const container = scrollContainer.value
  if (!container || !pageCount.value) return

  const pages = container.querySelectorAll('[data-pdf-page]')
  if (!pages.length) return

  // The final page may not be able to reach the top when it is shorter than
  // the viewport. Treat the bottom of the document as the final page.
  if (container.scrollHeight - container.scrollTop - container.clientHeight <= 2) {
    currentPage.value = pageCount.value
    return
  }

  const activationLine = container.getBoundingClientRect().top + Math.min(80, container.clientHeight * 0.2)
  const positions = Array.from(pages, (page) => ({
    pageNumber: Number(page.dataset.pdfPage),
    top: page.getBoundingClientRect().top
  }))
  currentPage.value = getPageAtScrollPosition(positions, activationLine)
}

function goToPage(pageNumber) {
  const container = scrollContainer.value
  if (!container || !pageCount.value) return

  const target = Math.min(Math.max(pageNumber, 1), pageCount.value)
  const page = container.querySelector(`[data-pdf-page="${target}"]`)
  if (!page) return

  const topPadding = Number.parseFloat(window.getComputedStyle(container).paddingTop) || 0
  const relativeTop = page.getBoundingClientRect().top - container.getBoundingClientRect().top
  const targetScrollTop = container.scrollTop + relativeTop - container.clientTop - topPadding

  // Use an immediate scroll. A smooth scroll passes through intermediate pages,
  // so its scroll events can overwrite currentPage during repeated button clicks.
  container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'auto' })
  currentPage.value = target
}

function download() {
  if (!sourceBlob || readyPdfKey !== createPdfCacheKey(props.reportId, props.templateId, props.templateVersion)) {
    throw new Error('PDF를 준비하는 중입니다.')
  }
  const url = URL.createObjectURL(sourceBlob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = sourceFilename
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function noteRenderError(message) {
  renderWarning.value = message
}

watch(
  () => [props.reportId, props.templateId, props.templateVersion],
  loadPdf,
  { immediate: true }
)
watch(scrollContainer, observeScrollContainer)
onMounted(() => {
  resizeObserver = new ResizeObserver(() => {
    if (isFitWidth.value) applyFitWidth()
  })
  observeScrollContainer(scrollContainer.value)
})
onBeforeUnmount(() => {
  loadVersion += 1
  pdfRequests.dispose()
  resizeObserver?.disconnect()
  clearPdf()
})

defineExpose({ download, reload: loadPdf })
</script>

<template>
  <section class="report-pdf-viewer" :aria-busy="isLoading">
    <div v-if="isLoading" class="report-pdf-viewer__status">
      <span class="loading-spinner" aria-hidden="true" /> {{ templateName }} PDF를 준비하고 있습니다.
    </div>
    <div v-else-if="error" class="report-pdf-viewer__status report-pdf-viewer__status--error" role="alert">
      <p>{{ error }}</p>
      <button type="button" @click="loadPdf">다시 시도</button>
    </div>
    <template v-else-if="pdfDocument">
      <div class="report-pdf-viewer__toolbar" aria-label="PDF 보기 도구">
        <div class="report-pdf-viewer__zoom-controls">
          <button type="button" aria-label="축소" :disabled="scale <= 0.4" @click="zoomBy(1 / 1.15)">−</button>
          <output>{{ zoomPercent }}</output>
          <button type="button" aria-label="확대" :disabled="scale >= 3" @click="zoomBy(1.15)">+</button>
          <button type="button" :class="{ 'is-active': isFitWidth }" @click="isFitWidth = true; applyFitWidth()">너비 맞춤</button>
        </div>
        <div class="report-pdf-viewer__page-controls">
          <button type="button" aria-label="이전 페이지" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">‹</button>
          <output>{{ currentPage }} / {{ pageCount }}</output>
          <button type="button" aria-label="다음 페이지" :disabled="currentPage >= pageCount" @click="goToPage(currentPage + 1)">›</button>
        </div>
      </div>
      <p v-if="renderWarning" class="report-pdf-viewer__warning" role="status">{{ renderWarning }}</p>
      <div ref="scrollContainer" class="report-pdf-viewer__pages" tabindex="0" aria-label="PDF 리포트 페이지" @scroll.passive="syncCurrentPageFromScroll">
        <PdfPageCanvas
          v-for="pageNumber in pageCount"
          :key="pageNumber"
          :pdf-document="pdfDocument"
          :page-number="pageNumber"
          :scale="scale"
          :base-page-size="basePageSize"
          :scroll-root="scrollContainer"
          :is-priority="pageNumber <= 2"
          @render-error="noteRenderError"
        />
      </div>
    </template>
  </section>
</template>

<style scoped>
.report-pdf-viewer { overflow: hidden; min-height: 680px; border: 1.5px solid var(--border-subtle); border-radius: var(--radius-card); background: var(--bg-subtle); box-shadow: var(--shadow-card); }
.report-pdf-viewer__status { display: grid; place-items: center; align-content: center; gap: 14px; min-height: 680px; padding: 32px; color: var(--text-secondary); font-size: var(--text-md); text-align: center; }
.report-pdf-viewer__status--error { color: var(--status-follow); }
.report-pdf-viewer__status p { margin: 0; }
.report-pdf-viewer__status button, .report-pdf-viewer__toolbar button { min-height: 42px; padding: 0 14px; border: 1.5px solid var(--border-default); border-radius: var(--radius-sm); color: var(--text-primary); background: var(--bg-surface); font: inherit; font-size: var(--text-sm); font-weight: 800; }
.report-pdf-viewer__status button { border-color: currentColor; color: inherit; }
.report-pdf-viewer__toolbar button:hover:not(:disabled), .report-pdf-viewer__toolbar button.is-active { border-color: var(--action-primary); color: var(--action-primary); background: var(--blue-100); }
.report-pdf-viewer__toolbar button:disabled { cursor: not-allowed; opacity: .45; }
.report-pdf-viewer__toolbar { position: sticky; top: 0; z-index: 2; display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 12px 16px; border-bottom: 1.5px solid var(--border-subtle); background: rgba(255, 255, 255, .96); backdrop-filter: blur(10px); }
.report-pdf-viewer__zoom-controls, .report-pdf-viewer__page-controls { display: flex; align-items: center; gap: 8px; }
.report-pdf-viewer__toolbar output { min-width: 62px; color: var(--text-secondary); font-size: var(--text-sm); font-weight: 800; text-align: center; }
.report-pdf-viewer__page-controls button { min-width: 42px; padding: 0; font-size: 27px; line-height: 1; }
.report-pdf-viewer__pages { display: flex; flex-direction: column; align-items: safe center; gap: 24px; height: min(78vh, 1100px); min-height: 620px; padding: 24px; overflow: auto; overscroll-behavior: contain; }
.report-pdf-viewer__warning { margin: 0; padding: 10px 16px; color: var(--status-care); background: var(--status-care-bg); font-size: var(--text-sm); font-weight: 700; }
@media (max-width: 640px) {
  .report-pdf-viewer, .report-pdf-viewer__status { min-height: 520px; border-radius: var(--radius-md); }
  .report-pdf-viewer__toolbar { align-items: stretch; flex-direction: column; padding: 10px; }
  .report-pdf-viewer__zoom-controls, .report-pdf-viewer__page-controls { justify-content: center; }
  .report-pdf-viewer__toolbar button { min-height: 40px; padding: 0 10px; }
  .report-pdf-viewer__pages { height: 72vh; min-height: 520px; padding: 16px 12px; gap: 16px; }
}
</style>
